// Crew use the mobile number the office registered. Artemis staff keep Microsoft SSO.
import { useState } from "react";
import { Button, Input } from "@aiw/ui";
import { useT } from "../i18n";
import { sendPhoneCode, signInWithMicrosoft, verifyPhoneCode } from "../lib/auth";

export function SignIn() {
  const { t, toggle } = useT();
  // MOBILE FIRST. The office holds the crew's number; the crew holds the phone. An email link needs
  // a mailbox nobody checks on a roof, which is how the first version failed on site.
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed" | "verifying">("idle");
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setState("sending");
    setError("");
    if (step === "phone") {
      const r = await sendPhoneCode(phone);
      if (r.ok) { setPhone(r.phone); setStep("code"); setState("sent"); }
      else { setError(r.error); setState("failed"); }
      return;
    }
    const r = await verifyPhoneCode(phone, code);
    if (r.ok) setState("verifying");
    else { setError(r.error); setState("failed"); }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-background px-4">
      <form onSubmit={(e) => void submit(e)} className="w-full max-w-sm rounded-lg border bg-card p-6 text-center">
        <img src="/brand/artemis-lockup-on-light.svg" alt="Artemis Flooring" width={300} height={56} className="mx-auto h-10 w-auto" />
        <h1 className="mt-4 text-xl font-semibold">{t("signin_title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("signin_phone_body")}</p>
        {step === "phone" && <>
          <label className="mt-4 block text-left text-sm font-medium" htmlFor="phone">{t("signin_phone")}</label>
          <Input id="phone" type="tel" inputMode="tel" autoComplete="tel" required placeholder="0412 345 678" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 h-11" />
          <Button type="submit" size="touch" className="mt-4 w-full" disabled={state === "sending" || !phone}>{t("signin_send_code")}</Button>
        </>}
        {step === "code" && <>
          <p role="status" className="mt-3 text-sm text-success">{t("signin_code_sent", { phone })}</p>
          <label className="mt-3 block text-left text-sm font-medium" htmlFor="code">{t("signin_code")}</label>
          <Input id="code" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} className="mt-1 h-11 text-center text-lg tracking-[0.3em]" />
          <Button type="submit" size="touch" className="mt-4 w-full" disabled={state === "sending" || code.trim().length < 4}>{t("signin_verify")}</Button>
          <button type="button" className="mt-3 min-h-[44px] text-sm text-muted-foreground underline-offset-2 hover:underline" onClick={() => { setStep("phone"); setCode(""); setState("idle"); }}>{t("signin_change_number")}</button>
        </>}
        {state === "verifying" && <p role="status" className="mt-3 text-sm text-success">{t("signin_welcome")}</p>}
        {state === "failed" && <p role="alert" className="mt-3 text-sm text-destructive">{t("signin_failed", { error })}</p>}
        <div className="my-4 border-t" />
        <Button type="button" size="touch" variant="outline" className="w-full" onClick={() => void signInWithMicrosoft().then(r=>{if(!r.ok){setError(r.error);setState('failed');}})}>{t("signin_staff_sso")}</Button>
        <p className="mt-2 text-micro text-muted-foreground">{t("signin_staff_sso_note")}</p>
        <button type="button" onClick={toggle} className="mt-4 min-h-[44px] text-sm text-muted-foreground underline-offset-2 hover:underline">{t("lang_toggle")}</button>
      </form>
    </main>
  );
}
